import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Activity,
  ArrowRight,
  BookOpen,
  Building2,
  Check,
  ChevronDown,
  CircleAlert,
  Clipboard,
  Database,
  FileDown,
  Gauge,
  LayoutDashboard,
  ListFilter,
  LogOut,
  Menu,
  NotebookPen,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Save,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  X,
} from "lucide-react";
import type {
  BusinessProfile,
  Lead,
  LeadStatus,
  ProspectorSettings,
  RelatedLink,
  SearchJob,
} from "@prospector/shared";
import { api, isDemo } from "./api";
import { signIn, signOut, signUp, confirmSignUp, resetPassword, confirmPassword } from "./auth";
import {
  demoJobs as localDemoJobs,
  demoLeads as localDemoLeads,
  demoProfile,
} from "./mock";

const statuses: LeadStatus[] = [
  "NEW",
  "RESEARCHING",
  "QUALIFIED",
  "REVIEW_REQUIRED",
  "APPROVED",
  "CONNECTION_SENT",
  "CONNECTED",
  "MESSAGE_SENT",
  "REPLIED",
  "MEETING_BOOKED",
  "TRIAL_PARTNER",
  "PAYING_PARTNER",
  "NOT_SUITABLE",
  "DO_NOT_CONTACT",
];
export const pipelineStatuses: LeadStatus[] = [...statuses];
const demoJobs = isDemo ? localDemoJobs : [];
const demoLeads = isDemo ? localDemoLeads : [];
const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/search", label: "New search", icon: Search },
  { to: "/jobs", label: "Search jobs", icon: Activity },
  { to: "/prospects", label: "Prospects", icon: Users },
  { to: "/pipeline", label: "Pipeline", icon: ListFilter },
  { to: "/business", label: "Business knowledge", icon: BookOpen },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/diagnostics", label: "Diagnostics", icon: Gauge },
];
type ProviderHealth = {
  status: string;
  service: string;
  providers: {
    bedrock: boolean;
    tavily: boolean;
    companiesHouse: boolean;
    crawler: boolean;
  };
};
function cx(...values: (string | false | undefined)[]) {
  return values.filter(Boolean).join(" ");
}
function scoreTone(score: number) {
  return score >= 85 ? "great" : score >= 70 ? "good" : "review";
}
function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: string;
}) {
  return <span className={`badge ${tone}`}>{children}</span>;
}
function Score({ value }: { value: number }) {
  return (
    <div className={`score ${scoreTone(value)}`}>
      <strong>{value}</strong>
      <span>fit</span>
    </div>
  );
}

function Login({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'login' | 'signup' | 'confirm' | 'reset' | 'resetConfirm'>('login');
  const [code, setCode] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email || (!['reset', 'confirm'].includes(mode) && !password)) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (mode === 'signup') { await signUp(email, password); setMode('confirm'); return; }
      if (mode === 'confirm') { await confirmSignUp(email, code); setMode('login'); return; }
      if (mode === 'reset') { await resetPassword(email); setMode('resetConfirm'); return; }
      if (mode === 'resetConfirm') { await confirmPassword(email, code, password); setMode('login'); return; }
      const token = await signIn(email, password);
      sessionStorage.setItem("prospector_token", token);
      sessionStorage.setItem("prospector_session", "true");
      onLogin();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="login-page">
      <section className="login-copy">
        <div className="brand-mark">
          <Sparkles /> Prospector
        </div>
        <h1>Find the people who can make a real difference.</h1>
        <p>
          Evidence-led prospecting for your business — thoughtful research,
          ranked opportunities and human outreach.
        </p>
        <div className="trust">
          <ShieldCheck />
          <span>Private AWS workspace · Human approval required</span>
        </div>
      </section>
      <section className="login-card">
        <p className="eyebrow">PRIVATE ACCESS</p>
        <h2>{({ login: 'Welcome back', signup: 'Create your account', confirm: 'Verify your email', reset: 'Reset your password', resetConfirm: 'Set a new password' })[mode]}</h2>
        <p>Sign in to your prospecting workspace.</p>
        <form onSubmit={submit}>
          <label>
            Email address
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@thecoparent.app"
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
            />
          </label>
          {['confirm', 'resetConfirm'].includes(mode) && <label>Email verification code<input value={code} onChange={e => setCode(e.target.value)} autoComplete="one-time-code" required /></label>}
          {error && <p className="error">{error}</p>}
          <button className="primary wide" disabled={busy}>
            {busy ? 'Please wait…' : ({ login: 'Sign in', signup: 'Create account', confirm: 'Verify email', reset: 'Send reset code', resetConfirm: 'Set new password' })[mode]} <ArrowRight size={18} />
          </button>
        </form>
        <button type="button" className="demo-link" onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); }}>{mode === 'login' ? 'Create account' : 'Back to sign in'}</button>
        <button type="button" className="demo-link" onClick={() => { setMode('reset'); setError(''); }}>Forgot password?</button>
        {isDemo && (
          <button
            className="demo-link"
            onClick={() => {
              sessionStorage.setItem("prospector_session", "true");
              onLogin();
            }}
          >
            Enter local demonstration
          </button>
        )}
      </section>
    </main>
  );
}

function Shell({
  children,
  onLogout,
}: {
  children: ReactNode;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="shell">
      <aside className={cx("sidebar", open && "open")}>
        <div className="brand">
          <span>
            <Sparkles />
          </span>
          <div>
            Prospector<small>THE COPARENT APP</small>
          </div>
        </div>
        <nav aria-label="Main navigation">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setOpen(false)}
            >
              <item.icon size={19} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="side-foot">
          <div className="privacy">
            <ShieldCheck />
            <span>
              Private workspace<small>Evidence-first research</small>
            </span>
          </div>
          <button onClick={onLogout}>
            <LogOut size={18} />
            Sign out
          </button>
        </div>
      </aside>
      <header className="mobile-head">
        <button aria-label="Open menu" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
        <span>
          <Sparkles /> Prospector
        </span>
      </header>
      <main className="content">
        {isDemo && (
          <div className="demo-banner">
            <CircleAlert size={16} />
            <span>
              Local demonstration — sample records are clearly fictional and are
              not research claims.
            </span>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
function PageHead({
  eyebrow,
  title,
  desc,
  actions,
}: {
  eyebrow: string;
  title: string;
  desc: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{desc}</p>
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}
function Stat({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string | number;
  detail: string;
  tone?: string;
}) {
  return (
    <article className="stat">
      <span>{label}</span>
      <strong className={tone}>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}
function LeadRow({ lead }: { lead: Lead }) {
  return (
    <article className="lead-row">
      <Score value={lead.score.overall} />
      <div className="lead-main">
        <div>
          <Badge tone="violet">{lead.sector}</Badge>
          <Badge tone={lead.status === "QUALIFIED" ? "green" : "neutral"}>
            {lead.status.replaceAll("_", " ")}
          </Badge>
          {lead.userNotes?.trim() && (
            <Badge tone="neutral">
              <NotebookPen size={12} /> Note added
            </Badge>
          )}
          {lead.duplicateOfLeadId && (
            <Badge tone="neutral">
              <CircleAlert size={12} /> Possible duplicate
            </Badge>
          )}
          {lead.qualityAssessment && (
            <Badge
              tone={
                lead.qualityAssessment.relevanceGate === "UNRELATED"
                  ? "neutral"
                  : lead.qualityAssessment.redFlags.length >= 3
                    ? "violet"
                    : "green"
              }
            >
              {lead.qualityAssessment.relevanceGate.replaceAll("_", " ")} ·{" "}
              {lead.qualityAssessment.redFlags.length} quality flag
              {lead.qualityAssessment.redFlags.length === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        <h3>{lead.personName || "Decision maker to confirm"}</h3>
        <p className="org">
          <Building2 size={15} />
          {lead.organisationName} · {lead.location || "Location unconfirmed"}
        </p>
        <p>{lead.summary}</p>
        {lead.qualityAssessment?.redFlags[0] && (
          <p className="quality-preview">
            <CircleAlert size={13} /> {lead.qualityAssessment.redFlags[0]}
          </p>
        )}
      </div>
      <div className="confidence">
        <span>Confidence</span>
        <strong>{lead.score.confidence}%</strong>
        <a href={lead.website} target="_blank" rel="noreferrer">
          Website <ArrowRight size={16} />
        </a>
        <Link to={`/prospects/${lead.id}`}>
          View report <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
}

function DashboardPage() {
  const [leads, setLeads] = useState(demoLeads);
  const [jobs, setJobs] = useState(demoJobs);
  useEffect(() => {
    if (!isDemo) {
      api<Lead[]>("/prospector/leads")
        .then(setLeads)
        .catch(() => {});
      api<SearchJob[]>("/prospector/search")
        .then(setJobs)
        .catch(() => {});
    }
  }, []);
  const newProspects = leads.filter((l) => l.status === "NEW").length;
  const paying = leads.filter((l) => l.status === "PAYING_PARTNER").length;
  const conversion = leads.length
    ? `${Math.round((paying / leads.length) * 1000) / 10}%`
    : "0%";
  const today = new Date()
    .toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
    })
    .toUpperCase();
  return (
    <>
      <PageHead
        eyebrow={today}
        title="Good morning, Alistair."
        desc="Here are the opportunities worth your attention today."
        actions={
          <Link className="primary" to="/search">
            <Plus size={18} />
            New search
          </Link>
        }
      />
      <section className="stats">
        <Stat
          label="Total prospects"
          value={leads.length}
          detail="Across all searches"
        />
        <Stat
          label="New"
          value={newProspects}
          detail="Awaiting your review"
          tone="mint"
        />
        <Stat
          label="Awaiting review"
          value={leads.filter((l) => l.status === "REVIEW_REQUIRED").length}
          detail="Needs your judgement"
        />
        <Stat
          label="Meetings booked"
          value={leads.filter((l) => l.status === "MEETING_BOOKED").length}
          detail="This month"
        />
        <Stat
          label="Partner conversion"
          value={conversion}
          detail="All prospects to paying partner"
          tone="mint"
        />
      </section>
      <section className="section-title">
        <div>
          <p className="eyebrow">PRIORITISED FOR YOU</p>
          <h2>Today’s Best Prospects</h2>
          <p>
            Strong matches with enough public evidence to support a thoughtful
            approach.
          </p>
        </div>
        <Link to="/prospects">View all prospects</Link>
      </section>
      <div className="lead-list">
        {leads
          .slice()
          .filter((lead) => lead.status !== "NOT_SUITABLE")
          .sort((a, b) => b.score.overall - a.score.overall)
          .slice(0, 3)
          .map((l) => (
            <LeadRow lead={l} key={l.id} />
          ))}
      </div>
      <div className="two-col">
        <section className="panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">SEARCH ACTIVITY</p>
              <h2>Recent jobs</h2>
            </div>
            <Link to="/jobs">See all</Link>
          </div>
          {jobs.slice(0, 3).map((job) => (
            <div className="job-mini" key={job.id}>
              <div>
                <strong>{job.term}</strong>
                <span>
                  {job.stage.replaceAll("_", " ")} · {job.leadCount} found
                </span>
              </div>
              <div className="progress">
                <i style={{ width: `${job.progress}%` }} />
              </div>
              <b>{job.progress}%</b>
            </div>
          ))}
        </section>
        <section className="panel insight">
          <p className="eyebrow">NEXT BEST ACTION</p>
          <Target />
          <h2>
            {newProspects
              ? `${newProspects} new prospect${newProspects === 1 ? " is" : "s are"} ready`
              : "Run your first live search"}
          </h2>
          <p>
            {newProspects
              ? "Review the evidence and outreach drafts, then approve only the prospects that match your partnership goals."
              : "Start with a professional sector and Prospector will build an evidence-backed shortlist."}
          </p>
          <Link to={newProspects ? "/prospects" : "/search"}>
            {newProspects ? "Review new prospects" : "Start research"}{" "}
            <ArrowRight size={16} />
          </Link>
        </section>
      </div>
    </>
  );
}

function SearchPage() {
  const navigate = useNavigate();
  const [term, setTerm] = useState("McKenzie Friends");
  const [advanced, setAdvanced] = useState(false);
  const [maxResults, setMaxResults] = useState(25);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (term.trim().length < 2) return;
    setBusy(true);
    setError("");
    try {
      if (isDemo) {
        await new Promise((r) => setTimeout(r, 700));
        navigate("/jobs");
      } else {
        await api("/prospector/search", {
          method: "POST",
          body: JSON.stringify({ term, limits: { maxResults } }),
        });
        navigate("/jobs");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Search could not start");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHead
        eyebrow="AUTONOMOUS RESEARCH"
        title="Who should we find?"
        desc="Give Prospector one plain-English target. It will plan the research, find organisations, verify evidence and rank the best opportunities."
      />
      <form className="search-hero" onSubmit={submit}>
        <label htmlFor="term">
          Search for a professional sector or organisation type
        </label>
        <div className="search-box">
          <Search />
          <input
            id="term"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="e.g. McKenzie Friends"
            autoFocus
          />
          <button className="primary" disabled={busy}>
            {busy ? (
              <>
                <RefreshCw className="spin" />
                Starting…
              </>
            ) : (
              <>
                <Play size={18} />
                Start research
              </>
            )}
          </button>
        </div>
        <div className="search-hints">
          <span>Try:</span>
          {[
            "Family mediators",
            "Family law solicitors",
            "Contact centres",
            "Domestic abuse organisations",
          ].map((x) => (
            <button type="button" key={x} onClick={() => setTerm(x)}>
              {x}
            </button>
          ))}
        </div>
        <button
          className="advanced-toggle"
          type="button"
          onClick={() => setAdvanced(!advanced)}
        >
          Advanced settings{" "}
          <ChevronDown className={advanced ? "rotated" : ""} size={17} />
        </button>
        {advanced && (
          <div className="advanced">
            <label>
              Maximum qualified leads
              <input
                type="number"
                min="1"
                max="100"
                value={maxResults}
                onChange={(e) => setMaxResults(Number(e.target.value))}
              />
            </label>
            <label>
              Primary geography
              <select defaultValue="United Kingdom">
                <option>United Kingdom</option>
                <option>Use business profile</option>
              </select>
            </label>
            <p>
              <CircleAlert size={17} />
              Estimated ceiling: £3.50. The job will stop at the configured
              result, request, model-call or time limit.
            </p>
          </div>
        )}
        {error && <p className="error">{error}</p>}
      </form>
      <section className="how">
        <p className="eyebrow">WHAT HAPPENS NEXT</p>
        <div>
          <span>01</span>
          <h3>Plans intelligently</h3>
          <p>
            Expands the sector into relevant phrases, organisation types and
            decision-maker roles.
          </p>
        </div>
        <div>
          <span>02</span>
          <h3>Researches carefully</h3>
          <p>
            Uses legitimate public sources, respects robots.txt and records
            every evidence URL.
          </p>
        </div>
        <div>
          <span>03</span>
          <h3>Ranks honestly</h3>
          <p>
            Separates confirmed facts, reasoned inferences and unknowns before
            suggesting outreach.
          </p>
        </div>
      </section>
    </>
  );
}

function JobsPage() {
  const [jobs, setJobs] = useState(demoJobs);
  useEffect(() => {
    if (!isDemo) api<SearchJob[]>("/prospector/search").then(setJobs);
  }, []);
  return (
    <>
      <PageHead
        eyebrow="RESEARCH OPERATIONS"
        title="Search jobs"
        desc="Follow each controlled research run from planning to final recommendations."
        actions={
          <Link className="primary" to="/search">
            <Plus size={18} />
            New search
          </Link>
        }
      />
      <div className="table-card">
        <div className="table-head">
          <span>Search</span>
          <span>Stage</span>
          <span>Progress</span>
          <span>Qualified</span>
          <span>Estimated cost</span>
        </div>
        {jobs.map((job) => (
          <div className="table-row" key={job.id}>
            <div>
              <strong>{job.term}</strong>
              <small>
                {new Date(job.createdAt).toLocaleDateString("en-GB")}
              </small>
            </div>
            <Badge tone={job.stage === "COMPLETE" ? "green" : "violet"}>
              {job.stage.replaceAll("_", " ")}
            </Badge>
            <div className="job-progress">
              <div className="progress">
                <i style={{ width: `${job.progress}%` }} />
              </div>
              <span>{job.progress}%</span>
            </div>
            <strong>{job.qualifiedCount}</strong>
            <span>£{job.estimatedCostGbp.toFixed(2)}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function ProspectsPage() {
  const [leads, setLeads] = useState(demoLeads);
  const [filter, setFilter] = useState("ALL");
  const [query, setQuery] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    if (!isDemo) api<Lead[]>("/prospector/leads").then(setLeads);
  }, []);
  const activeLeads = leads.filter((lead) => lead.status !== "NOT_SUITABLE");
  const filtered =
    filter === "ALL"
      ? activeLeads
      : activeLeads.filter((l) => l.status === filter);
  const searchTerms = query.trim().toLowerCase();
  const shown = searchTerms
    ? filtered.filter((lead) =>
        [
          lead.personName,
          lead.organisationName,
          lead.role,
          lead.sector,
          lead.location,
          lead.userNotes,
        ].some((value) => String(value ?? "").toLowerCase().includes(searchTerms)),
      )
    : filtered;
  async function addWebsite(event: FormEvent) {
    event.preventDefault();
    setAdding(true);
    setAddError("");
    try {
      const result = await api<{
        leadId: string;
        duplicate: boolean;
        status: string;
      }>("/prospector/leads/research", {
        method: "POST",
        body: JSON.stringify({
          websiteUrl: websiteUrl.trim(),
          organisationName: organisationName.trim() || undefined,
        }),
      });
      navigate(`/prospects/${result.leadId}`);
    } catch (caught) {
      setAddError(
        caught instanceof Error ? caught.message : "Research could not be started",
      );
    } finally {
      setAdding(false);
    }
  }
  function exportCsv() {
    const rows = [
      [
        "Name",
        "Organisation",
        "Sector",
        "Location",
        "Score",
        "Confidence",
        "Status",
        "Website",
      ],
      ...shown.map((l) => [
        l.personName ?? "",
        l.organisationName,
        l.sector,
        l.location ?? "",
        String(l.score.overall),
        String(l.score.confidence),
        l.status,
        l.website,
      ]),
    ];
    const blob = new Blob(
      [
        rows
          .map((r) => r.map((v) => `"${v.replaceAll('"', '""')}"`).join(","))
          .join("\n"),
      ],
      { type: "text/csv" },
    );
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "prospector-leads.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }
  return (
    <>
      <PageHead
        eyebrow="RANKED OPPORTUNITIES"
        title="Prospects"
        desc={`${shown.length} evidence-backed opportunities, ordered by commercial fit.`}
        actions={
          <button className="secondary" onClick={exportCsv}>
            <FileDown size={18} />
            Export CSV
          </button>
        }
      />
      <div className="panel website-research-panel">
        <div>
          <p className="eyebrow">ADD A SPECIFIC PROSPECT</p>
          <h2>Research a website and prepare for the call</h2>
          <p>
            Paste an organisation’s website. Prospector will add it immediately,
            research its public pages in the background and prepare fit, outreach
            and an Emma-ready call brief.
          </p>
        </div>
        <form onSubmit={addWebsite}>
          <input
            type="url"
            required
            value={websiteUrl}
            onChange={(event) => setWebsiteUrl(event.target.value)}
            placeholder="https://organisation.org.uk"
            aria-label="Prospect website"
          />
          <input
            value={organisationName}
            onChange={(event) => setOrganisationName(event.target.value)}
            placeholder="Organisation name (optional)"
            aria-label="Organisation name"
          />
          <button className="primary" disabled={adding}>
            <Sparkles size={16} />
            {adding ? "Starting…" : "Research website"}
          </button>
        </form>
        {addError && <p className="form-error">{addError}</p>}
      </div>
      <div className="prospect-search">
        <Search size={18} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search names, organisations, sectors, roles or your notes…"
          aria-label="Search prospects"
        />
        {query && (
          <button onClick={() => setQuery("")} aria-label="Clear search">
            <X size={15} />
          </button>
        )}
      </div>
      <div className="filters">
        <button
          className={filter === "ALL" ? "active" : ""}
          onClick={() => setFilter("ALL")}
        >
          All
        </button>
        {[
          "NEW",
          "QUALIFIED",
          "REVIEW_REQUIRED",
          "APPROVED",
          "DO_NOT_CONTACT",
        ].map(
          (s) => (
            <button
              className={filter === s ? "active" : ""}
              onClick={() => setFilter(s)}
              key={s}
            >
              {s.replaceAll("_", " ")}
            </button>
          ),
        )}
      </div>
      <div className="lead-list">
        {shown.map((l) => (
          <LeadRow lead={l} key={l.id} />
        ))}
        {!shown.length && (
          <Empty
            title={
              query
                ? `No prospects match “${query}”`
                : "No prospects in this view"
            }
          />
        )}
      </div>
    </>
  );
}

function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | undefined>(() =>
    demoLeads.find((l) => l.id === id),
  );
  const [copied, setCopied] = useState("");
  const [researchQueued, setResearchQueued] = useState(false);
  useEffect(() => {
    if (!isDemo && id) api<Lead>(`/prospector/leads/${id}`).then(setLead);
  }, [id]);
  useEffect(() => {
    if (
      isDemo ||
      !id ||
      (!researchQueued && lead?.status !== "RESEARCHING")
    )
      return;
    const timer = window.setInterval(() => {
      api<Lead>(`/prospector/leads/${id}`)
        .then((updated) => {
          setLead(updated);
          if (updated.callBrief && updated.status !== "RESEARCHING")
            setResearchQueued(false);
        })
        .catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [id, lead?.status, researchQueued]);
  if (!lead) return <Empty title="Prospect not found" />;
  async function changeStatus(status: LeadStatus) {
    if (!isDemo)
      await api(`/prospector/leads/${lead!.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
    setLead((current) => (current ? { ...current, status } : current));
  }
  async function markNotSuitable() {
    await changeStatus("NOT_SUITABLE");
    navigate("/prospects");
  }
  async function saveProspect(changes: Record<string, unknown>) {
    if (isDemo) {
      setLead((current) =>
        current ? ({ ...current, ...changes } as Lead) : current,
      );
      return;
    }
    const updated = await api<Lead>(`/prospector/leads/${lead!.id}`, {
      method: "PATCH",
      body: JSON.stringify(changes),
    });
    setLead(updated);
  }
  async function copy(text: string, label: string) {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 1600);
  }
  async function refreshResearch() {
    if (isDemo) return;
    await api(`/prospector/leads/${lead!.id}/research`, {
      method: "POST",
      body: JSON.stringify({ websiteUrl: lead!.website }),
    });
    setLead((current) =>
      current ? { ...current, callBrief: undefined } : current,
    );
    setResearchQueued(true);
  }
  return (
    <>
      <PageHead
        eyebrow={`${lead.sector} · ${lead.location ?? "Location unknown"}`}
        title={lead.personName || lead.organisationName}
        desc={`${lead.role ?? "Decision maker"} at ${lead.organisationName}`}
        actions={
          <>
            <button
              className="secondary"
              onClick={refreshResearch}
              disabled={researchQueued || lead.status === "RESEARCHING"}
            >
              <RefreshCw size={15} />
              {researchQueued || lead.status === "RESEARCHING"
                ? "Researching…"
                : lead.callBrief
                  ? "Refresh research"
                  : "Prepare call brief"}
            </button>
            <select
              aria-label="Pipeline status"
              value={lead.status}
              onChange={(e) => changeStatus(e.target.value as LeadStatus)}
            >
              {statuses.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </>
        }
      />
      <div className="detail-grid">
        <section>
          {lead.duplicateOfLeadId && (
            <div className="panel duplicate-warning">
              <CircleAlert size={20} />
              <div>
                <p className="eyebrow">DUPLICATE CHECK</p>
                <h2>
                  {lead.duplicateReason === "SAME_PERSON"
                    ? "This person may already be in Prospector"
                    : "Another contact from this organisation already exists"}
                </h2>
                <Link to={`/prospects/${lead.duplicateOfLeadId}`}>
                  Compare with the existing profile <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          )}
          <ContactDetailsEditor lead={lead} onSave={saveProspect} />
          <div className="panel fit-panel">
            <Score value={lead.score.overall} />
            <div>
              <p className="eyebrow">
                AI FIT ASSESSMENT{" "}
                {lead.fitAssessment && (
                  <Badge
                    tone={
                      lead.fitAssessment.verdict === "STRONG_FIT"
                        ? "green"
                        : "violet"
                    }
                  >
                    {lead.fitAssessment.verdict.replaceAll("_", " ")}
                  </Badge>
                )}
                {lead.fitAssessment?.opportunityType && (
                  <Badge tone="neutral">
                    {lead.fitAssessment.opportunityType.replaceAll("_", " ")}
                  </Badge>
                )}
              </p>
              <h2>{lead.summary}</h2>
              {lead.fitAssessment && (
                <div className="assessment-detail">
                  <div>
                    <strong>Why it fits</strong>
                    <ul>
                      {lead.fitAssessment.whyItFits.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                  {lead.fitAssessment.concerns.length > 0 && (
                    <div>
                      <strong>Concerns and unknowns</strong>
                      <ul>
                        {lead.fitAssessment.concerns.map((concern) => (
                          <li key={concern}>{concern}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <strong>Recommended approach</strong>
                    <p>{lead.fitAssessment.recommendedApproach}</p>
                  </div>
                  {lead.fitAssessment.valueExchange && (
                    <div>
                      <strong>Potential value exchange</strong>
                      <p>{lead.fitAssessment.valueExchange}</p>
                    </div>
                  )}
                  {lead.fitAssessment.businessCapabilitiesUsed?.length > 0 && (
                    <div>
                      <strong>Relevant parts of the business</strong>
                      <ul>
                        {lead.fitAssessment.businessCapabilitiesUsed.map(
                          (capability) => (
                            <li key={capability}>{capability}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <p>
                Confidence {lead.score.confidence}% ·{" "}
                {lead.score.recommendedNextAction}
              </p>
            </div>
          </div>
          {lead.qualityAssessment && (
            <QualityAssessmentPanel quality={lead.qualityAssessment} />
          )}
          <div className="panel">
            <h2>Why this prospect matches</h2>
            <ul className="reason-list">
              {lead.score.positiveReasons.map((r) => (
                <li key={r}>
                  <Check /> {r}
                </li>
              ))}
            </ul>
            {lead.score.negativeReasons.map((r) => (
              <p className="caution" key={r}>
                <CircleAlert /> {r}
              </p>
            ))}
          </div>
          <UsefulLinks lead={lead} />
          <div className="panel">
            <div className="panel-head">
              <h2>Evidence</h2>
              <Badge tone="green">
                {lead.evidence.length} sourced claim
                {lead.evidence.length !== 1 ? "s" : ""}
              </Badge>
            </div>
            {lead.evidence.map((e) => (
              <article className="evidence" key={e.url}>
                <Badge
                  tone={e.classification === "CONFIRMED" ? "green" : "neutral"}
                >
                  {e.classification}
                </Badge>
                <p>{e.claim}</p>
                <a href={e.url} target="_blank" rel="noreferrer">
                  {e.title} <ArrowRight size={14} />
                </a>
                <small>
                  Checked {new Date(e.checkedAt).toLocaleDateString("en-GB")}
                </small>
              </article>
            ))}
          </div>
        </section>
        <aside>
          <ProspectNotes lead={lead} onSave={saveProspect} />
          {lead.callBrief ? (
            <div className="panel call-brief">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">CALL PREPARATION</p>
                  <h2>Emma’s conversation brief</h2>
                </div>
                <small>
                  Updated{" "}
                  {new Date(lead.callBrief.preparedAt).toLocaleDateString(
                    "en-GB",
                  )}
                </small>
              </div>
              <p>{lead.callBrief.organisationOverview}</p>
              <div className="call-objective">
                <strong>Goal for the call</strong>
                <p>{lead.callBrief.callObjective}</p>
              </div>
              <div>
                <strong>Suggested opening</strong>
                <p>{lead.callBrief.suggestedOpening}</p>
              </div>
              <BriefList
                title="Things worth talking about"
                items={lead.callBrief.talkingPoints}
              />
              <BriefList
                title="Questions to ask"
                items={lead.callBrief.discoveryQuestions}
              />
              <BriefList
                title="Signals to listen for"
                items={lead.callBrief.signalsToListenFor}
              />
              <BriefList
                title="Before the call"
                items={lead.callBrief.preparationChecklist}
              />
              <div className="brief-sources">
                <strong>Research sources</strong>
                {lead.callBrief.sourceUrls.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer">
                    {new URL(url).hostname}
                    <ArrowRight size={12} />
                  </a>
                ))}
              </div>
            </div>
          ) : (
            <div className="panel call-brief-empty">
              <p className="eyebrow">CALL PREPARATION</p>
              <h2>
                {researchQueued || lead.status === "RESEARCHING"
                  ? "Research in progress"
                  : "No call brief yet"}
              </h2>
              <p>
                {researchQueued || lead.status === "RESEARCHING"
                  ? "You can leave this page. The brief will appear when the website research is complete."
                  : "Use Prepare call brief above to research this website and generate specific talking points."}
              </p>
            </div>
          )}
          {lead.outreach ? (
            <>
              <div className="panel outreach">
                <p className="eyebrow">RECOMMENDED ANGLE</p>
                <h2>{lead.outreach.pitchAngle}</h2>
              </div>
              <Message
                title="LinkedIn connection"
                text={lead.outreach.connectionRequest}
                characterLimit={200}
                onCopy={() =>
                  copy(lead.outreach!.connectionRequest, "connection")
                }
                copied={copied === "connection"}
              />
              <Message
                title="LinkedIn follow-up"
                text={lead.outreach.linkedinFollowUp}
                onCopy={() => copy(lead.outreach!.linkedinFollowUp, "followup")}
                copied={copied === "followup"}
              />
              <Message
                title={lead.outreach.emailSubject}
                text={lead.outreach.emailBody}
                onCopy={() => copy(lead.outreach!.emailBody, "email")}
                copied={copied === "email"}
              />
            </>
          ) : (
            <div className="panel">
              <h2>Outreach pending</h2>
              <p>The evidence needs review before copy is generated.</p>
            </div>
          )}
          {lead.status !== "NOT_SUITABLE" && (
            <button className="secondary archive-link" onClick={markNotSuitable}>
              <X size={15} /> Mark not suitable
            </button>
          )}
          <button
            className="danger-link"
            onClick={() => changeStatus("DO_NOT_CONTACT")}
          >
            Mark do not contact
          </button>
        </aside>
      </div>
    </>
  );
}
function QualityAssessmentPanel({
  quality,
}: {
  quality: NonNullable<Lead["qualityAssessment"]>;
}) {
  const labels: Record<keyof typeof quality.dimensions, string> = {
    missionRelevance: "Mission relevance",
    audienceOverlap: "Audience overlap",
    partnershipPotential: "Partnership potential",
    websiteQuality: "Website quality",
    businessMaturity: "Business maturity",
    domainAuthority: "Domain authority proxy",
    independentReputation: "Independent reputation",
    evidenceConfidence: "Evidence confidence",
  };
  return (
    <div className="panel quality-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">HUMAN FILTERING SCORE</p>
          <h2>Fit, legitimacy and quality checks</h2>
        </div>
        <Badge
          tone={quality.relevanceGate === "UNRELATED" ? "neutral" : "green"}
        >
          {quality.relevanceGate}
        </Badge>
      </div>
      <p>{quality.qualitySummary}</p>
      <div className="quality-dimensions">
        {(Object.entries(quality.dimensions) as Array<
          [keyof typeof quality.dimensions, number]
        >).map(([key, value]) => (
          <div key={key}>
            <span>{labels[key]}</span>
            <div className="quality-bar">
              <i style={{ width: `${value}%` }} />
            </div>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      <div className="quality-facts">
        <span>
          Domain age: {quality.domainAgeYears === undefined ? "Unknown" : `${quality.domainAgeYears.toFixed(1)} years`}
        </span>
        <span>Independent reviews found: {quality.independentReviewCount}</span>
        <span>Independent mentions: {quality.independentMentionCount}</span>
        <span>
          Company status: {quality.companyStatus || "Not verified"}
        </span>
      </div>
      {quality.redFlags.length > 0 && (
        <div className="quality-flags">
          <strong>Red flags and missing trust signals</strong>
          <ul>
            {quality.redFlags.map((flag) => (
              <li key={flag}>
                <CircleAlert size={13} /> {flag}
              </li>
            ))}
          </ul>
        </div>
      )}
      {quality.positiveSignals.length > 0 && (
        <div className="quality-positive">
          <strong>Positive trust signals</strong>
          <ul>
            {quality.positiveSignals.map((signal) => (
              <li key={signal}>
                <Check size={13} /> {signal}
              </li>
            ))}
          </ul>
        </div>
      )}
      <small>
        Domain authority is a transparent proxy based on domain maturity and
        independent web presence, not a proprietary Moz/Ahrefs score.
      </small>
    </div>
  );
}
function BriefList({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div>
      <strong>{title}</strong>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}
function ContactDetailsEditor({
  lead,
  onSave,
}: {
  lead: Lead;
  onSave: (changes: Record<string, unknown>) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [organisationName, setOrganisationName] = useState(
    lead.organisationName,
  );
  const [personName, setPersonName] = useState(lead.personName ?? "");
  const [role, setRole] = useState(lead.role ?? "");
  const [linkedInUrl, setLinkedInUrl] = useState(lead.linkedInUrl ?? "");
  const [email, setEmail] = useState(lead.email ?? "");
  const [phone, setPhone] = useState(lead.phone ?? "");
  const [contactFormUrl, setContactFormUrl] = useState(
    lead.contactFormUrl ?? "",
  );
  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await onSave({
        organisationName: organisationName.trim(),
        personName: personName.trim() || null,
        role: role.trim() || null,
        linkedInUrl: linkedInUrl.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        contactFormUrl: contactFormUrl.trim() || null,
      });
      setEditing(false);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Details could not be saved",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="panel prospect-editor">
      <div className="panel-head">
        <div>
          <p className="eyebrow">YOUR CONTACT</p>
          <h2>Prospect details</h2>
        </div>
        {!editing && (
          <button className="secondary compact" onClick={() => setEditing(true)}>
            <Pencil size={14} /> Edit details
          </button>
        )}
      </div>
      {editing ? (
        <form onSubmit={save} className="prospect-form">
          <label>
            Organisation name
            <input
              required
              value={organisationName}
              onChange={(event) => setOrganisationName(event.target.value)}
              placeholder="e.g. LawWorks"
            />
          </label>
          <label>
            Contact name
            <input
              value={personName}
              onChange={(event) => setPersonName(event.target.value)}
              placeholder="e.g. Ayşen Soyer"
            />
          </label>
          <label>
            Role
            <input
              value={role}
              onChange={(event) => setRole(event.target.value)}
              placeholder="e.g. Head of Family Law"
            />
          </label>
          <label>
            LinkedIn profile
            <input
              type="url"
              value={linkedInUrl}
              onChange={(event) => setLinkedInUrl(event.target.value)}
              placeholder="https://www.linkedin.com/in/..."
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@organisation.co.uk"
            />
          </label>
          <label>
            Phone
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="Public business number"
            />
          </label>
          <label>
            Contact page
            <input
              type="url"
              value={contactFormUrl}
              onChange={(event) => setContactFormUrl(event.target.value)}
              placeholder="https://..."
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button
              type="button"
              className="secondary"
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
            <button className="primary" disabled={saving}>
              <Save size={15} /> {saving ? "Saving…" : "Save details"}
            </button>
          </div>
        </form>
      ) : (
        <div className="contact-summary">
          <div>
            <small>Organisation</small>
            <strong>{lead.organisationName}</strong>
            <span>{lead.sector}</span>
          </div>
          <div>
            <small>Contact</small>
            <strong>{lead.personName || "Not identified yet"}</strong>
            <span>{lead.role || "Role not recorded"}</span>
          </div>
          <div className="contact-routes">
            <small>Ways to connect</small>
            {lead.linkedInUrl && (
              <a href={lead.linkedInUrl} target="_blank" rel="noreferrer">
                LinkedIn profile <ArrowRight size={14} />
              </a>
            )}
            {lead.email && <a href={`mailto:${lead.email}`}>{lead.email}</a>}
            {lead.phone && <a href={`tel:${lead.phone}`}>{lead.phone}</a>}
            {!lead.linkedInUrl && !lead.email && !lead.phone && (
              <span>No direct contact route recorded</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
function ProspectNotes({
  lead,
  onSave,
}: {
  lead: Lead;
  onSave: (changes: Record<string, unknown>) => Promise<void>;
}) {
  const [notes, setNotes] = useState(lead.userNotes ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  async function save() {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await onSave({ userNotes: notes.trim() || null });
      setSaved(true);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Notes could not be saved",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="panel prospect-notes">
      <div className="panel-head">
        <div>
          <p className="eyebrow">PRIVATE NOTES</p>
          <h2>Your notes</h2>
        </div>
        {lead.userNotes?.trim() && (
          <Badge tone="violet">
            <NotebookPen size={12} /> Added
          </Badge>
        )}
      </div>
      <textarea
        value={notes}
        onChange={(event) => {
          setNotes(event.target.value);
          setSaved(false);
        }}
        maxLength={10000}
        placeholder="Add anything useful you found, who to contact, or what to do next…"
      />
      <div className="notes-footer">
        <small>
          {saved
            ? "Saved"
            : lead.notesUpdatedAt
              ? `Last saved ${new Date(lead.notesUpdatedAt).toLocaleString("en-GB")}`
              : `${notes.length}/10,000 characters`}
        </small>
        <button className="primary compact" onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? "Saving…" : "Save notes"}
        </button>
      </div>
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
function UsefulLinks({ lead }: { lead: Lead }) {
  const fallbackLinks: RelatedLink[] = [
    {
      url: lead.website,
      title: `${lead.organisationName} website`,
      type: "WEBSITE",
    },
    ...(lead.linkedInUrl
      ? [
          {
            url: lead.linkedInUrl,
            title: "LinkedIn profile",
            type: "LINKEDIN" as const,
          },
        ]
      : []),
    ...(lead.contactFormUrl
      ? [
          {
            url: lead.contactFormUrl,
            title: "Contact page",
            type: "CONTACT" as const,
          },
        ]
      : []),
    ...lead.evidence.map((e) => ({
      url: e.url,
      title: e.title,
      type: "SOURCE" as const,
    })),
  ];
  const links = [
    ...new Map(
      [...(lead.relatedLinks ?? []), ...fallbackLinks].map((link) => [
        link.url,
        link,
      ]),
    ).values(),
  ];
  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">IDENTITY CHECK</p>
          <h2>Profiles and useful links</h2>
        </div>
        <Badge tone="violet">{links.length} links</Badge>
      </div>
      <p className="link-guidance">
        Open these sources to confirm the person, role and organisation before
        making contact.
      </p>
      <div className="useful-links">
        {links.map((link) => (
          <a href={link.url} target="_blank" rel="noreferrer" key={link.url}>
            <span>
              <Badge
                tone={
                  link.type === "LINKEDIN" || link.type === "PROFILE"
                    ? "green"
                    : "neutral"
                }
              >
                {link.type.replaceAll("_", " ")}
              </Badge>
              <strong>{link.title}</strong>
              <small>{new URL(link.url).hostname.replace(/^www\./, "")}</small>
            </span>
            <ArrowRight size={17} />
          </a>
        ))}
      </div>
    </div>
  );
}
function Message({
  title,
  text,
  characterLimit,
  onCopy,
  copied,
}: {
  title: string;
  text: string;
  characterLimit?: number;
  onCopy: () => void;
  copied: boolean;
}) {
  return (
    <div className="panel message">
      <div className="panel-head">
        <h3>{title}</h3>
        <button onClick={onCopy}>
          {copied ? <Check /> : <Clipboard />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p>{text}</p>
      <small>
        Draft only · Review before sending manually
        {characterLimit ? ` · ${text.length}/${characterLimit} characters` : ""}
      </small>
    </div>
  );
}

function PipelinePage() {
  const [leads, setLeads] = useState(demoLeads);
  useEffect(() => {
    if (!isDemo) api<Lead[]>("/prospector/leads").then(setLeads);
  }, []);
  return (
    <>
      <PageHead
        eyebrow="RELATIONSHIP PIPELINE"
        title="From research to partnership"
        desc="Every movement is recorded in the prospect timeline."
      />
      <div className="pipeline">
        {pipelineStatuses.map((status) => (
          <section
            key={status}
            className={status === "NOT_SUITABLE" || status === "DO_NOT_CONTACT" ? "pipeline-rejected" : ""}
          >
            <div className="pipe-head">
              <span>{status.replaceAll("_", " ")}</span>
              <b>{leads.filter((l) => l.status === status).length}</b>
            </div>
            {leads
              .filter((l) => l.status === status)
              .map((l) => (
                <Link
                  to={`/prospects/${l.id}`}
                  className="pipe-card"
                  key={l.id}
                >
                  <strong>{l.personName || "Decision maker to confirm"}</strong>
                  <span>{l.organisationName}</span>
                  <div className="pipe-card-meta">
                    <Badge tone="violet">{l.score.overall} fit</Badge>
                    {l.userNotes?.trim() && (
                      <Badge tone="neutral">
                        <NotebookPen size={11} /> Note
                      </Badge>
                    )}
                    {l.duplicateOfLeadId && (
                      <Badge tone="neutral">
                        <CircleAlert size={11} /> Duplicate
                      </Badge>
                    )}
                  </div>
                </Link>
              ))}
          </section>
        ))}
      </div>
    </>
  );
}
function BusinessPage() {
  const [profile, setProfile] = useState<BusinessProfile>(demoProfile);
  const [url, setUrl] = useState(profile.websiteUrl);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!isDemo)
      api<BusinessProfile | null>("/prospector/business")
        .then((value) => {
          if (value) {
            setProfile(value);
            setUrl(value.websiteUrl);
          }
        })
        .catch((e) =>
          setError(
            e instanceof Error ? e.message : "Profile could not be loaded",
          ),
        );
  }, []);
  function update<K extends keyof BusinessProfile>(
    key: K,
    value: BusinessProfile[K],
  ) {
    setProfile((current) => ({ ...current, [key]: value }));
  }
  async function toggleEdit() {
    if (editing && !isDemo)
      await api("/prospector/business", {
        method: "PUT",
        body: JSON.stringify({ manualOverrides: profile }),
      });
    setEditing(!editing);
  }
  async function crawl() {
    setBusy(true);
    setError("");
    const previous = profile.lastCrawledAt;
    try {
      if (!isDemo) {
        await api("/prospector/business/crawl", {
          method: "POST",
          body: JSON.stringify({ websiteUrl: url }),
        });
        for (let attempt = 0; attempt < 30; attempt++) {
          await new Promise((r) => setTimeout(r, 3000));
          const value = await api<BusinessProfile | null>(
            "/prospector/business",
          );
          if (value && value.lastCrawledAt !== previous) {
            setProfile(value);
            setUrl(value.websiteUrl);
            return;
          }
        }
        throw new Error(
          "The refresh is still running. Reopen this page in a minute.",
        );
      }
      await new Promise((r) => setTimeout(r, 600));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Website refresh failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHead
        eyebrow="PERSISTENT BUSINESS BRAIN"
        title="Business Knowledge"
        desc="The evidence Prospector retrieves before every search and analysis."
        actions={
          <button className="secondary" onClick={toggleEdit}>
            {editing ? "Save corrections" : "Correct profile"}
          </button>
        }
      />
      {error && <p className="error">{error}</p>}
      <section className="knowledge-source panel">
        <div>
          <span className="source-icon">
            <Database />
          </span>
          <div>
            <h3>{profile.businessName}</h3>
            <a href={profile.websiteUrl}>{profile.websiteUrl}</a>
            <small>
              Last checked{" "}
              {new Date(profile.lastCrawledAt).toLocaleDateString("en-GB")} ·
              Prompt {profile.promptVersion}
            </small>
          </div>
        </div>
        <div className="crawl-form">
          <input
            aria-label="Website URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button className="primary" onClick={crawl} disabled={busy}>
            <RefreshCw className={busy ? "spin" : ""} />
            {busy ? "Analysing website…" : "Refresh profile & score"}
          </button>
        </div>
      </section>
      {profile.websiteAudit ? (
        <section className="panel reverse-audit">
          <div className="reverse-audit-head">
            <div>
              <p className="eyebrow">REVERSE PROSPECT SCORE</p>
              <h2>How potential partners see your website</h2>
              <p>{profile.websiteAudit.summary}</p>
            </div>
            <div className={`reverse-score ${scoreTone(profile.websiteAudit.overallScore)}`}>
              <strong>{profile.websiteAudit.overallScore}</strong>
              <span>/ 100</span>
              <small>{profile.websiteAudit.verdict.replace("_", " ")}</small>
            </div>
          </div>
          <p className="audit-explainer">
            This applies the same sceptical checks used for prospects, viewed from
            the perspective of your configured target sectors. Domain authority is
            a transparent maturity and independent-mention proxy, not a proprietary
            Moz or Ahrefs score.
          </p>
          <div className="audit-dimensions">
            {Object.entries(profile.websiteAudit.dimensions).map(([key, value]) => (
              <div className="audit-dimension" key={key}>
                <span>{key.replace(/([A-Z])/g, " $1")}</span>
                <div><i style={{ width: `${value}%` }} /></div>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
          <div className="reverse-audit-columns">
            <div>
              <h3>Highest-impact improvements</h3>
              <div className="audit-improvements">
                {profile.websiteAudit.improvements.map((item) => (
                  <article key={item.title}>
                    <div>
                      <Badge tone={item.priority === "HIGH" ? "danger" : item.priority === "MEDIUM" ? "review" : "neutral"}>{item.priority}</Badge>
                      <strong>{item.title}</strong>
                      <span>Potential impact +{item.estimatedImpact}</span>
                    </div>
                    <p>{item.reason}</p>
                    <small>{item.action}</small>
                  </article>
                ))}
              </div>
            </div>
            <div>
              <h3>Partner viewpoints</h3>
              <div className="partner-perspectives">
                {profile.websiteAudit.partnerPerspectives.map((item) => (
                  <article key={item.sector}>
                    <div><strong>{item.sector}</strong><Badge tone={scoreTone(item.likelyFitScore)}>{item.likelyFitScore}% likely fit</Badge></div>
                    <p>{item.impression}</p>
                    {item.concerns[0] && <small>Concern: {item.concerns[0]}</small>}
                  </article>
                ))}
              </div>
            </div>
          </div>
          <div className="audit-signals">
            <span>Domain age <strong>{profile.websiteAudit.domainAgeYears === undefined ? "Not found" : `${profile.websiteAudit.domainAgeYears.toFixed(1)} years`}</strong></span>
            <span>Companies House <strong>{profile.websiteAudit.companyStatus ?? "No exact match"}</strong></span>
            <span>Independent reviews <strong>{profile.websiteAudit.independentReviewCount}</strong></span>
            <span>Independent mentions <strong>{profile.websiteAudit.independentMentionCount}</strong></span>
          </div>
        </section>
      ) : (
        <section className="panel reverse-audit-empty">
          <Gauge />
          <div>
            <h3>Reverse prospect score</h3>
            <p>Refresh the website to see how organisations in your target industries are likely to assess its relevance, credibility and partnership readiness.</p>
          </div>
        </section>
      )}
      <div className="knowledge-grid">
        <Knowledge
          title="Mission"
          text={profile.mission}
          editable={editing}
          onText={(value) => update("mission", value)}
        />
        <Knowledge
          title="Product proposition"
          text={profile.productDescription}
          editable={editing}
          onText={(value) => update("productDescription", value)}
        />
        <Knowledge
          title="Professional partnership"
          text={profile.partnershipProposition}
          editable={editing}
          onText={(value) => update("partnershipProposition", value)}
        />
        <Knowledge
          title="Founder name"
          text={profile.founderName}
          editable={editing}
          onText={(value) => update("founderName", value)}
        />
        <Knowledge
          title="Founder role"
          text={profile.founderRole}
          editable={editing}
          onText={(value) => update("founderRole", value)}
        />
        <Knowledge
          title="Founder story"
          text={profile.founderStory}
          editable={editing}
          onText={(value) => update("founderStory", value)}
        />
        <Knowledge
          title="Target sectors"
          tags={profile.professionalTargetSectors}
          editable={editing}
          onTags={(value) => update("professionalTargetSectors", value)}
        />
        <Knowledge
          title="Main features"
          tags={profile.mainFeatures}
          editable={editing}
          onTags={(value) => update("mainFeatures", value)}
        />
        <Knowledge
          title="Partnership models"
          tags={profile.partnershipModels}
          editable={editing}
          onTags={(value) => update("partnershipModels", value)}
        />
        <Knowledge
          title="Free access and sponsorship"
          tags={profile.freeAccessOptions}
          editable={editing}
          onTags={(value) => update("freeAccessOptions", value)}
        />
        <Knowledge
          title="Sales and outreach angles"
          tags={profile.salesAngles}
          editable={editing}
          onTags={(value) => update("salesAngles", value)}
        />
        <Knowledge
          title="Audience-specific value"
          tags={(profile.audienceValuePropositions ?? []).map(
            (item) => `${item.audience}: ${item.value}`,
          )}
          editable={false}
        />
        <Knowledge
          title="Brand voice"
          tags={profile.brandVoice}
          editable={editing}
          onTags={(value) => update("brandVoice", value)}
        />
        <Knowledge
          title="Claims to avoid"
          tags={profile.claimsToAvoid}
          warning
          editable={editing}
          onTags={(value) => update("claimsToAvoid", value)}
        />
        <Knowledge
          title="Geographic market"
          tags={profile.geographicMarket}
          editable={editing}
          onTags={(value) => update("geographicMarket", value)}
        />
        <Knowledge
          title="Relevant terminology"
          tags={profile.terminology}
          editable={editing}
          onTags={(value) => update("terminology", value)}
        />
      </div>
      <section className="panel manual-note">
        <ShieldCheck />
        <div>
          <h3>Manual corrections always win</h3>
          <p>
            Anything you correct here is stored separately and reapplied after
            future crawls. Source material remains available for audit.
          </p>
        </div>
      </section>
    </>
  );
}
function Knowledge({
  title,
  text,
  tags,
  warning,
  editable,
  onText,
  onTags,
}: {
  title: string;
  text?: string;
  tags?: string[];
  warning?: boolean;
  editable: boolean;
  onText?: (value: string) => void;
  onTags?: (value: string[]) => void;
}) {
  return (
    <article className="panel knowledge">
      <div className="panel-head">
        <h3>{title}</h3>
        {editable && <Badge tone="violet">Editing</Badge>}
      </div>
      {text &&
        (editable ? (
          <textarea
            value={text}
            onChange={(event) => onText?.(event.target.value)}
          />
        ) : (
          <p>{text}</p>
        ))}
      {tags &&
        (editable ? (
          <textarea
            aria-label={`${title}, one item per line`}
            value={tags.join("\n")}
            onChange={(event) =>
              onTags?.(
                event.target.value
                  .split("\n")
                  .map((value) => value.trim())
                  .filter(Boolean),
              )
            }
          />
        ) : (
          <div className="tag-list">
            {tags.map((t) => (
              <span className={warning ? "warn" : ""} key={t}>
                {t}
              </span>
            ))}
          </div>
        ))}
    </article>
  );
}
const defaultProspectorSettings: ProspectorSettings = {
  maxResults: 25,
  maxSearchRequests: 12,
  maxPagesPerSite: 8,
  maxBedrockCalls: 40,
  maxDurationMinutes: 15,
  dailyBudgetGbp: 10,
};
function SavedSettingsPage() {
  const [settings, setSettings] = useState(defaultProspectorSettings);
  const [health, setHealth] = useState<ProviderHealth | null>(
    isDemo
      ? {
          status: "ok",
          service: "demo",
          providers: {
            bedrock: true,
            tavily: false,
            companiesHouse: false,
            crawler: true,
          },
        }
      : null,
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!isDemo) {
      api<ProspectorSettings>("/prospector/settings")
        .then(setSettings)
        .catch((error) =>
          setMessage(
            error instanceof Error
              ? error.message
              : "Settings could not be loaded",
          ),
        );
      api<ProviderHealth>("/prospector/health")
        .then(setHealth)
        .catch(() => setHealth(null));
    }
  }, []);
  function update(key: keyof ProspectorSettings, value: number) {
    setSettings((current) => ({ ...current, [key]: value }));
    setMessage("");
  }
  async function save() {
    if (isDemo) {
      setMessage("Settings are not saved in the local demonstration.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const saved = await api<ProspectorSettings>("/prospector/settings", {
        method: "PUT",
        body: JSON.stringify(settings),
      });
      setSettings(saved);
      setMessage("Settings saved. New searches will use these limits.");
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Settings could not be saved",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <>
      <PageHead
        eyebrow="CONTROL CENTRE"
        title="Settings"
        desc="Safe defaults for research quality, cost and providers."
        actions={
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save settings"}
          </button>
        }
      />
      {message && (
        <p
          className={
            message.startsWith("Settings saved") ? "settings-note" : "error"
          }
        >
          {message}
        </p>
      )}
      <div className="settings-grid">
        <section className="panel">
          <h2>Search budgets</h2>
          <NumberSetting
            label="Maximum qualified leads"
            value={settings.maxResults}
            min={1}
            max={100}
            onChange={(value) => update("maxResults", value)}
          />
          <NumberSetting
            label="Web-search requests per job"
            value={settings.maxSearchRequests}
            min={1}
            max={50}
            onChange={(value) => update("maxSearchRequests", value)}
          />
          <NumberSetting
            label="Pages per prospect website"
            value={settings.maxPagesPerSite}
            min={1}
            max={30}
            onChange={(value) => update("maxPagesPerSite", value)}
          />
          <NumberSetting
            label="Bedrock calls per job"
            value={settings.maxBedrockCalls}
            min={1}
            max={150}
            onChange={(value) => update("maxBedrockCalls", value)}
          />
          <NumberSetting
            label="Maximum job duration (minutes)"
            value={settings.maxDurationMinutes}
            min={1}
            max={60}
            onChange={(value) => update("maxDurationMinutes", value)}
          />
          <NumberSetting
            label="Daily budget warning (£)"
            value={settings.dailyBudgetGbp}
            min={0}
            max={10000}
            step={0.5}
            onChange={(value) => update("dailyBudgetGbp", value)}
          />
        </section>
        <section className="panel">
          <h2>Provider status</h2>
          <Provider
            name="Amazon Bedrock"
            ok={Boolean(health?.providers.bedrock)}
          />
          <Provider
            name="Tavily Search"
            ok={Boolean(health?.providers.tavily)}
          />
          <Provider
            name="Companies House"
            ok={Boolean(health?.providers.companiesHouse)}
          />
          <Provider
            name="Direct website crawler"
            ok={Boolean(health?.providers.crawler)}
          />
          <p className="settings-note">
            API keys are held in encrypted AWS Lambda environment variables and
            are never sent to this browser.
          </p>
        </section>
        <section className="panel">
          <h2>Compliance guardrails</h2>
          {[
            "Respect robots.txt",
            "Public business data only",
            "Human approval before contact",
            "No authenticated LinkedIn scraping",
            "Suppression list is retained",
          ].map((x) => (
            <p className="check-line" key={x}>
              <Check />
              {x}
            </p>
          ))}
        </section>
      </div>
    </>
  );
}
function NumberSetting({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="setting">
      <span>{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}
function SettingsPage() {
  const [health, setHealth] = useState<ProviderHealth | null>(
    isDemo
      ? {
          status: "ok",
          service: "demo",
          providers: {
            bedrock: true,
            tavily: false,
            companiesHouse: false,
            crawler: true,
          },
        }
      : null,
  );
  useEffect(() => {
    if (!isDemo)
      api<ProviderHealth>("/prospector/health")
        .then(setHealth)
        .catch(() => setHealth(null));
  }, []);
  return (
    <>
      <PageHead
        eyebrow="CONTROL CENTRE"
        title="Settings"
        desc="Safe defaults for research quality, cost and providers."
      />
      <div className="settings-grid">
        <section className="panel">
          <h2>Search budgets</h2>
          <Setting label="Maximum qualified leads" value="25" />
          <Setting label="Web-search requests per job" value="12" />
          <Setting label="Pages per prospect website" value="8" />
          <Setting label="Bedrock calls per job" value="40" />
          <Setting label="Maximum job duration" value="15 minutes" />
          <Setting label="Daily budget warning" value="£10.00" />
        </section>
        <section className="panel">
          <h2>Provider status</h2>
          <Provider
            name="Amazon Bedrock"
            ok={Boolean(health?.providers.bedrock)}
          />
          <Provider
            name="Tavily Search"
            ok={Boolean(health?.providers.tavily)}
          />
          <Provider
            name="Companies House"
            ok={Boolean(health?.providers.companiesHouse)}
          />
          <Provider
            name="Direct website crawler"
            ok={Boolean(health?.providers.crawler)}
          />
          <p className="settings-note">
            API keys are held in encrypted AWS Lambda environment variables and
            are never sent to this browser.
          </p>
        </section>
        <section className="panel">
          <h2>Compliance guardrails</h2>
          {[
            "Respect robots.txt",
            "Public business data only",
            "Human approval before contact",
            "No authenticated LinkedIn scraping",
            "Suppression list is retained",
          ].map((x) => (
            <p className="check-line" key={x}>
              <Check />
              {x}
            </p>
          ))}
        </section>
      </div>
    </>
  );
}
function Setting({ label, value }: { label: string; value: string }) {
  return (
    <label className="setting">
      <span>{label}</span>
      <input defaultValue={value} />
    </label>
  );
}
function Provider({ name, ok }: { name: string; ok: boolean }) {
  return (
    <div className="provider">
      <span>
        <i className={ok ? "on" : ""} />
        {name}
      </span>
      <Badge tone={ok ? "green" : "neutral"}>
        {ok ? "Ready" : "Not configured"}
      </Badge>
    </div>
  );
}
export { SettingsPage };
function DiagnosticsPage() {
  const [healthy, setHealthy] = useState(isDemo);
  const [jobs, setJobs] = useState(demoJobs);
  useEffect(() => {
    if (!isDemo) {
      api<ProviderHealth>("/prospector/health")
        .then(() => setHealthy(true))
        .catch(() => setHealthy(false));
      api<SearchJob[]>("/prospector/search")
        .then(setJobs)
        .catch(() => setJobs([]));
    }
  }, []);
  const failures = jobs.filter((job) => job.stage === "FAILED");
  return (
    <>
      <PageHead
        eyebrow="SYSTEM HEALTH"
        title="Diagnostics"
        desc="Operational signals without secrets or personal data."
      />
      <div className="diag-grid">
        <Stat
          label="API"
          value={healthy ? "Healthy" : "Unavailable"}
          detail="Live authenticated check"
          tone={healthy ? "mint" : undefined}
        />
        <Stat
          label="Search workflow"
          value={failures.length ? "Attention needed" : "Operational"}
          detail={`${failures.length} recorded failure${failures.length === 1 ? "" : "s"}`}
          tone={failures.length ? undefined : "mint"}
        />
        <Stat
          label="Crawler"
          value={healthy ? "Operational" : "Unknown"}
          detail="Robots policy active"
          tone={healthy ? "mint" : undefined}
        />
      </div>
      <section className="panel logs">
        <div className="panel-head">
          <h2>Recent search jobs</h2>
          <Badge tone={failures.length ? "neutral" : "green"}>
            {failures.length ? "Review failures" : "No attention needed"}
          </Badge>
        </div>
        {jobs.slice(0, 6).map((job) => (
          <div className="log-row" key={job.id}>
            <code>{new Date(job.updatedAt).toLocaleTimeString("en-GB")}</code>
            <code>{job.term}</code>
            <Badge tone={job.stage === "COMPLETE" ? "green" : "violet"}>
              {job.stage}
            </Badge>
            <span>
              {job.leadCount} prospects · {job.progress}%
            </span>
          </div>
        ))}
      </section>
    </>
  );
}
function Empty({ title }: { title: string }) {
  return (
    <div className="empty">
      <Target />
      <h2>{title}</h2>
      <p>There is nothing to show here yet.</p>
    </div>
  );
}

export function App() {
  const [signedIn, setSignedIn] = useState(() =>
    isDemo
      ? sessionStorage.getItem("prospector_session") === "true"
      : Boolean(sessionStorage.getItem("prospector_token")),
  );
  if (!signedIn) return <Login onLogin={() => setSignedIn(true)} />;
  return (
    <Shell
      onLogout={() => {
        signOut();
        setSignedIn(false);
      }}
    >
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/results" element={<Navigate to="/prospects" replace />} />
        <Route path="/prospects" element={<ProspectsPage />} />
        <Route path="/prospects/:id" element={<ProspectDetail />} />
        <Route path="/pipeline" element={<PipelinePage />} />
        <Route path="/business" element={<BusinessPage />} />
        <Route path="/settings" element={<SavedSettingsPage />} />
        <Route path="/diagnostics" element={<DiagnosticsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
